import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Tag, Gavel, ArrowLeftRight, Clock, X } from 'lucide-react';
import { useListings } from '@/hooks/useApi';
import { Listing } from '@/types';
import { formatPrice, timeUntil } from '@/utils';
import CardTypeFilter, { CardTypeValue } from '@/components/CardTypeFilter';
import CardImage from '@/components/CardImage';
import SEO from '@/components/SEO';

const LISTING_TYPES = [
  { value: 'all',     label: 'All Listings', icon: Tag          },
  { value: 'sale',    label: 'Buy Now',      icon: Tag          },
  { value: 'auction', label: 'Auctions',     icon: Gavel        },
  { value: 'trade',   label: 'Trades',       icon: ArrowLeftRight},
];

const CONDITIONS = ['Mint', 'Near Mint', 'Lightly Played', 'Moderately Played', 'Heavily Played'];

export default function MarketplacePage() {
  const [searchParams] = useSearchParams();
  const [activeListingType, setActiveListingType] = useState(() => {
    const t = searchParams.get('type');
    return LISTING_TYPES.some((l) => l.value === t) ? t! : 'all';
  });
  const [activeCardType, setActiveCardType]       = useState<CardTypeValue>('all');
  const [search, setSearch]                       = useState('');
  const [condition, setCondition]                 = useState('');
  const [maxPrice, setMaxPrice]                   = useState('');

  useEffect(() => {
    const t = searchParams.get('type');
    if (t && LISTING_TYPES.some((l) => l.value === t)) setActiveListingType(t);
  }, [searchParams]);

  const { data: listingsRaw, isLoading } = useListings({
    listing_type: activeListingType !== 'all' ? activeListingType : undefined,
    card_type:    activeCardType    !== 'all' ? activeCardType    : undefined,
    search:       search            || undefined,
    condition:    condition         || undefined,
    max_price:    maxPrice ? Number(maxPrice) : undefined,
  });
  const listings = (listingsRaw as any) as Listing[] | undefined;

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      <SEO
        url="/marketplace"
        description="Buy, sell, and trade Pokemon, Magic: The Gathering, Yu-Gi-Oh!, and more. Browse auctions, buy-now listings, and trade offers."
      />
      {/* ── Hero ──────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-brand-600 to-accent-500 text-white py-12">
        <div className="container-custom">
          <h1 className="text-4xl font-display font-bold mb-2">Marketplace</h1>
          <p className="text-brand-100 text-lg mb-5">
            Buy, sell, trade, and bid on cards from collectors worldwide
          </p>

          {/* Card type pills above search — no scrolling */}
          <CardTypeFilter
            active={activeCardType}
            onChange={setActiveCardType}
            variant="light"
          />

          {/* Search */}
          <div className="relative mt-4 max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 dark:text-zinc-500" />
            <input
              type="text"
              placeholder="Search listings…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-10 py-3 rounded-xl text-gray-900 dark:text-zinc-100 text-base shadow-lg dark:shadow-black/40 focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-400 dark:text-zinc-400"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="container-custom py-8">
        {/* Listing-type tabs (sale / auction / trade) */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {LISTING_TYPES.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              onClick={() => setActiveListingType(value)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${
                activeListingType === value
                  ? 'bg-brand-600 text-white shadow-md shadow-brand-200'
                  : 'bg-white dark:bg-zinc-900 text-gray-600 dark:text-zinc-400 border border-gray-200 dark:border-zinc-800 hover:border-brand-300 hover:text-brand-600 dark:text-brand-400'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Condition + price filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <select
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
            className="border border-gray-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm bg-white dark:bg-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand-400"
          >
            <option value="">Any Condition</option>
            {CONDITIONS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <div className="flex items-center gap-2 bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg px-3 py-2">
            <span className="text-sm text-gray-500 dark:text-zinc-500">Max $</span>
            <input
              type="number"
              placeholder="Price"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              className="w-20 text-sm focus:outline-none"
            />
          </div>
        </div>

        {/* Listings Grid */}
        {isLoading ? (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="skeleton h-80 rounded-xl" />
            ))}
          </div>
        ) : listings?.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-5xl mb-4">🏷️</p>
            <h3 className="text-xl font-bold text-gray-700 dark:text-zinc-300 mb-2">No listings found</h3>
            <p className="text-gray-500 dark:text-zinc-500 mb-6">Be the first to list a card!</p>
            <Link to="/listings/create" className="btn-primary">
              Create a Listing
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {listings?.map((listing: Listing) => (
              <Link
                key={listing.id}
                to={`/listings/${listing.id}`}
                className="card-hover group bg-white dark:bg-zinc-900 rounded-xl overflow-hidden shadow-sm border border-gray-200 dark:border-zinc-800"
              >
                <div className="aspect-[3/4] bg-gradient-to-br from-zinc-800 to-gray-200 relative overflow-hidden">
                  <CardImage
                    src={listing.image_urls?.[0]}
                    alt={listing.card?.name}
                    cardName={listing.card?.name}
                    cardNumber={listing.card?.card_number}
                    game={listing.card?.game}
                    className="w-full h-full group-hover:scale-105 transition-transform duration-200"
                  />
                  <div
                    className={`absolute top-2 left-2 px-2 py-1 rounded-lg text-xs font-bold flex items-center gap-1 ${
                      (listing.listing_type as string) === 'auction'
                        ? 'bg-orange-500 text-white'
                        : (listing.listing_type as string) === 'trade'
                        ? 'bg-purple-500 text-white'
                        : 'bg-green-500 text-white'
                    }`}
                  >
                    {(listing.listing_type as string) === 'auction' && (
                      <><Gavel className="h-3 w-3" /> Auction</>
                    )}
                    {(listing.listing_type as string) === 'trade' && (
                      <><ArrowLeftRight className="h-3 w-3" /> Trade</>
                    )}
                    {(listing.listing_type as string) === 'sale' && (
                      <><Tag className="h-3 w-3" /> Buy Now</>
                    )}
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="font-semibold text-sm truncate text-gray-900 dark:text-zinc-100">{listing.card?.name ?? listing.title}</h3>
                  <p className="text-xs text-gray-500 dark:text-zinc-500 mb-1">
                    {listing.condition} · {listing.card?.set_name}
                  </p>
                  {(listing.listing_type as string) === 'auction' ? (
                    <div>
                      <p className="text-brand-600 dark:text-brand-400 font-bold">
                        {listing.current_bid
                          ? `Current: ${formatPrice(listing.current_bid)}`
                          : `Start: ${formatPrice(listing.starting_price)}`}
                      </p>
                      {listing.ends_at && (
                        <p className="text-xs text-orange-500 flex items-center gap-1 mt-0.5">
                          <Clock className="h-3 w-3" /> {timeUntil(listing.ends_at)}
                        </p>
                      )}
                    </div>
                  ) : (listing.listing_type as string) === 'trade' ? (
                    <p className="text-purple-600 font-semibold text-sm">Open to offers</p>
                  ) : (
                    <p className="text-brand-600 dark:text-brand-400 font-bold">{formatPrice(listing.price)}</p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Floating Create Listing Button */}
      <div className="fixed bottom-20 right-4 sm:bottom-8 sm:right-8">
        <Link
          to="/listings/create"
          className="btn-primary shadow-xl flex items-center gap-2 rounded-full px-6 py-3 text-base"
        >
          + List a Card
        </Link>
      </div>
    </div>
  );
}
